import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { PreviewFormDataProvider } from '../../components/oasis/previewClinic';
import {
  PREVIEW_VIEWPORT_WIDTHS,
  type PreviewViewport,
} from './previewViewport';

/**
 * Text of a `<style>` whose rules were inserted through CSSOM rather than
 * written into the tag.
 *
 * Emotion (react-select, via `VirtualizedFormSelect`) ships inside the
 * prebuilt design system with `isDevelopment = false`, so it runs in speedy
 * mode: rules go in with `sheet.insertRule` and the tag itself stays empty.
 * Cloning such a tag copies nothing, which left emotion class names in the
 * frame with no CSS behind them.
 */
function styleText(el: HTMLElement): string {
  if (el.textContent) return el.textContent;
  const sheet = (el as HTMLStyleElement).sheet;
  if (!sheet) return '';
  try {
    return Array.from(sheet.cssRules)
      .map(rule => rule.cssText)
      .join('');
  } catch {
    // Cross-origin sheets refuse `cssRules`; nothing to mirror.
    return '';
  }
}

/**
 * How many rules the host's stylesheets hold right now.
 *
 * Emotion inserts through `sheet.insertRule`, which is not a DOM mutation, so
 * a `MutationObserver` never sees rules arriving in a tag it already saw
 * created. Counting them is the cheap way to notice.
 */
function hostRuleCount(): number {
  let total = 0;
  document.head.querySelectorAll('style').forEach(el => {
    const sheet = (el as HTMLStyleElement).sheet;
    try {
      total += sheet ? sheet.cssRules.length : 0;
    } catch {
      // Cross-origin sheet; not mirrored anyway.
    }
  });
  return total;
}

/** Mirror the host document's stylesheets into the iframe head. */
function syncStyles(frameDoc: Document): void {
  const head = frameDoc.head;
  head.querySelectorAll('[data-preview-style]').forEach(el => el.remove());
  document.head
    .querySelectorAll('style, link[rel="stylesheet"]')
    .forEach(el => {
      const clone = el.cloneNode(true) as HTMLElement;
      if (clone.tagName === 'STYLE') {
        clone.textContent = styleText(el as HTMLElement);
      }
      clone.setAttribute('data-preview-style', '');
      head.appendChild(clone);
    });
  // Carry over the theme class (e.g. `dark`) so tokens resolve the same way.
  frameDoc.documentElement.className = document.documentElement.className;
}

type PreviewFrameProps = {
  viewport: PreviewViewport;
  containerClassName?: string;
  children: ReactNode;
};

export function PreviewFrame({
  viewport,
  containerClassName,
  children,
}: PreviewFrameProps) {
  const [frame, setFrame] = useState<HTMLIFrameElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const width = PREVIEW_VIEWPORT_WIDTHS[viewport];

  useEffect(() => {
    if (!frame) return;

    let observer: MutationObserver | undefined;
    let frameObserver: MutationObserver | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let ruleCount = 0;

    // Builds (or rebuilds) the portal root inside the frame document. Runs on
    // `load` too, so an HMR-triggered frame reload re-establishes the portal
    // instead of leaving a detached container behind.
    const setup = () => {
      const frameDoc = frame.contentDocument;
      if (!frameDoc) return;

      syncStyles(frameDoc);
      // Vite HMR swaps <style> tags on every CSS edit; keep the frame current.
      observer?.disconnect();
      observer = new MutationObserver(() => {
        ruleCount = hostRuleCount();
        syncStyles(frameDoc);
      });
      observer.observe(document.head, { childList: true, subtree: true });

      // A control that styles itself through CSSOM as it renders (emotion, via
      // react-select) puts its rules in a tag the frame already mirrored, so
      // the observer above stays silent and the control paints unstyled.
      //
      // That rendering is itself a DOM change *inside the frame*, so the frame
      // is the trigger: re-mirror when its content changes and the host has
      // gained rules. Watching the frame's body while writing only to its head
      // cannot feed itself, and the rule count keeps a keystroke that changes
      // no styles from re-cloning anything.
      ruleCount = hostRuleCount();
      frameObserver?.disconnect();
      frameObserver = new MutationObserver(() => {
        const next = hostRuleCount();
        if (next === ruleCount) return;
        ruleCount = next;
        syncStyles(frameDoc);
      });
      frameObserver.observe(frameDoc.body, { childList: true, subtree: true });

      frameDoc.body.className = 'bg-background text-foreground';
      frameDoc.body.style.margin = '0';

      const root = frameDoc.createElement('div');
      root.setAttribute('data-preview-root', '');
      frameDoc.body.replaceChildren(root);

      // Grow the frame to fit its content so the preview scrolls with the page,
      // the way it did before it was framed. A fixed-height frame would clip the
      // form and strand an inner scrollbar.
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(() => {
        setContentHeight(prev => {
          const next = root.scrollHeight;
          // Ignore sub-pixel churn: setting the height resizes the frame, which
          // would otherwise re-trigger this observer forever.
          return prev != null && Math.abs(prev - next) < 2 ? prev : next;
        });
      });
      resizeObserver.observe(root);

      setContainer(root);
    };

    // The iframe has no `src`/`srcDoc`: it stays on the same-origin `about:blank`
    // document it is created with. Giving it a `srcDoc` would navigate the frame
    // *after* this effect runs and silently discard the portal root.
    setup();
    frame.addEventListener('load', setup);

    return () => {
      observer?.disconnect();
      frameObserver?.disconnect();
      resizeObserver?.disconnect();
      frame.removeEventListener('load', setup);
      setContainer(null);
    };
  }, [frame]);

  useEffect(() => {
    if (container) container.className = containerClassName ?? '';
  }, [container, containerClassName]);

  return (
    <iframe
      ref={setFrame}
      title='Form preview'
      className={[
        'block min-h-[24rem] bg-background',
        // Nothing decorative on the constrained widths — no border in
        // particular, which sits inside the box and would shrink the frame's
        // viewport below the requested width, flipping `md:` off at the boundary.
        width == null ? 'w-full' : 'mx-auto',
      ].join(' ')}
      style={{
        ...(width == null ? null : { width }),
        ...(contentHeight == null ? null : { height: contentHeight }),
      }}
    >
      {container
        ? createPortal(
            // Everything previewed renders outside a visit, so the fields that
            // need one read their stand-in data here rather than each preview
            // remembering to provide it.
            <PreviewFormDataProvider>{children}</PreviewFormDataProvider>,
            container
          )
        : null}
    </iframe>
  );
}
